import React from 'react'
import classNames from 'classnames'
import { Icon } from '@/ui/atoms/Icon'
import { useTranslations } from 'next-intl'
import { Divider } from '@/ui/atoms/Divider'

export type CoreStageCardProps = {
  title: string
  iconName: string
  description?: string
  contents?: string[]
  className?: string
}

export const CoreStageCard: React.FC<CoreStageCardProps> = ({
  title,
  iconName,
  description,
  contents,
  className: classNameFromProps,
}) => {
  const t = useTranslations('cards')

  return (
    <div
      className={classNames(
        '@container/core-stage hover:bg-bg-subtle hover:cursor-pointer bg-white p-6 md:p-8 rounded-md flex flex-col justify-between items-start border-[1px] border-border-subtle',
        classNameFromProps,
      )}
    >
      <div className={'flex flex-row justify-start items-center gap-4 mb-10'}>
        <div
          className={
            'size-8 rounded-sm border-[1px] border-border-subtle flex items-center justify-center relative overflow-hidden bg-gradient-to-b from-blue-40 to-blue-80'
          }
        >
          <Icon name={iconName} parentClass={'z-20'} className={'text-neutral-100 !size-5'} />
          <svg
            className={'absolute inset-0 z-10'}
            width="63"
            height="58"
            viewBox="0 0 63 58"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect
              y="8.21289"
              width="16.4331"
              height="49.2993"
              rx="1.36942"
              fill="white"
              fillOpacity="0.1"
            />
            <rect
              x="19.1797"
              y="16.4297"
              width="24.6496"
              height="41.0827"
              rx="1.36942"
              fill="white"
              fillOpacity="0.1"
            />
            <rect
              x="46.5625"
              y="16.4297"
              width="16.4331"
              height="41.0827"
              rx="1.36942"
              fill="white"
              fillOpacity="0.1"
            />
            <rect
              x="19.1797"
              y="8.21289"
              width="43.8216"
              height="5.4777"
              rx="1.36942"
              fill="white"
              fillOpacity="0.1"
            />
            <rect width="62.9935" height="5.4777" rx="1.36942" fill="white" fillOpacity="0.1" />
          </svg>
        </div>
        <h3 className={'text-headline-S-semibold text-text-base mb-0'}>{title}</h3>
      </div>
      <p className={'text-body-XS-regular text-text-subtle'}>{description}</p>
      <Divider className={'mt-10 mb-2'} />

      <div
        className={
          'flex flex-row justify-between items-end @lg/core-stage:items-start gap-6 w-full'
        }
      >
        <div className={'flex flex-col @lg/core-stage:flex-row gap-2 @lg/core-stage:gap-6'}>
          {contents && contents.length > 0 && (
            <React.Fragment>
              <span className={'text-body-XS-regular text-text-base'}>{t('contents')}:</span>
              <div className={'inline-flex flex-wrap justify-start gap-2'}>
                {contents.map((content, index) => (
                  <div key={index} className={'flex flex-row justify-center items-center'}>
                    <div className={'size-2 rounded-full bg-soft-gradient-purple me-1'}></div>
                    <div className={'text-label-XS-regular text-text-subtle leading-none'}>
                      {content}
                    </div>
                  </div>
                ))}
              </div>
            </React.Fragment>
          )}
        </div>
        <span
          className={
            'flex flex-row items-center justify-center text-label-S-semibold text-text-accent'
          }
        >
          <span className={'whitespace-nowrap'}>{t('read-more')}</span>
          <Icon name={'arrow-right-s-line'} className={'ms-1 !size-5'} />
        </span>
      </div>
    </div>
  )
}
